import React from "react";
import FeatherIcon from "feather-icons-react";

const C = { border: "#E4E9F2", muted: "#64748B", text: "#0F1F35" };

/** Icon + colour per audit action, so the shape of a history is readable at a glance. */
const ACTION_META = {
  created: { icon: "plus-circle", color: "#2E7D32", label: "Recorded" },
  updated: { icon: "edit-3", color: "#1565C0", label: "Edited" },
  voided: { icon: "x-circle", color: "#C62828", label: "Voided" },
  verified: { icon: "check-circle", color: "#0F6E56", label: "Verified by accounting" },
  unverified: { icon: "alert-circle", color: "#B45309", label: "Verification removed" },
  attachment_added: { icon: "paperclip", color: "#6A1B9A", label: "Attached" },
  attachment_removed: { icon: "paperclip", color: "#8D6E63", label: "Attachment removed" },
  archived: { icon: "archive", color: "#5F5E5A", label: "Archived with the tenancy" },
};

const stamp = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Everything that has happened to one PAD entry, newest first.
 *
 * Read-only by construction: the history is the record that a change was made and by whom, so
 * there is nothing here to edit. Oldest-last matches how people read a changelog — "what
 * happened most recently" is almost always the question.
 */
const PadHistory = ({ audit = [] }) => {
  const rows = [...(audit || [])].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));

  if (!rows.length) {
    return <div style={{ fontSize: 12, color: C.muted, padding: "6px 0" }}>No history recorded for this entry.</div>;
  }

  return (
    <div>
      <style>{`
        .ph-item { display: flex; gap: 10px; padding: 8px 0; border-bottom: 1px dashed ${C.border}; }
        .ph-item:last-child { border-bottom: none; }
        .ph-dot { width: 24px; height: 24px; border-radius: 999px; flex: none; display: inline-flex;
          align-items: center; justify-content: center; background: #F1F5F9; }
        .ph-body { flex: 1; min-width: 0; }
        .ph-head { font-size: 12.5px; font-weight: 600; color: ${C.text}; }
        .ph-when { font-size: 11.5px; color: ${C.muted}; font-weight: 500; }
        .ph-note { font-size: 12px; color: ${C.muted}; margin-top: 2px; }
        .ph-change { font-size: 12px; color: ${C.text}; margin-top: 3px; display: flex; flex-wrap: wrap; gap: 6px; align-items: baseline; }
        .ph-field { color: ${C.muted}; }
        .ph-from { text-decoration: line-through; color: #94A3B8; }
        .ph-to { font-weight: 600; }
      `}</style>

      {rows.map((a, i) => {
        const meta = ACTION_META[a.action] || { icon: "circle", color: C.muted, label: a.action || "Changed" };
        return (
          <div className="ph-item" key={`${a.action}-${a.at}-${i}`}>
            <span className="ph-dot" style={{ color: meta.color }}>
              <FeatherIcon icon={meta.icon} size={13} />
            </span>
            <div className="ph-body">
              <div className="ph-head">
                {meta.label}
                {a.by_name ? <span style={{ fontWeight: 500 }}> · {a.by_name}</span> : null}
                <span className="ph-when"> · {stamp(a.at)}</span>
              </div>
              {a.note ? <div className="ph-note">{a.note}</div> : null}
              {(a.changes || []).map((c, j) => (
                <div className="ph-change" key={j}>
                  <span className="ph-field">{c.label}</span>
                  <span className="ph-from">{c.from}</span>
                  <FeatherIcon icon="arrow-right" size={11} color="#94A3B8" />
                  <span className="ph-to">{c.to}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PadHistory;
